// src/pages/SocieteUsers.js - VERSION ADAPTÉE AU NOUVEAU BACKEND AVEC CHARTE GRAPHIQUE
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const SocieteUsers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const [societe, setSociete] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    search: queryParams.get('search') || '',
    profile: queryParams.get('profile') || '',
    statut: queryParams.get('statut') || ''
  });
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);

  // Réinitialisation du mot de passe (voir users/reset_password.php, déjà
  // autorisé pour l'admin sur n'importe quel utilisateur — seule cette page
  // n'exposait aucun bouton pour le déclencher).
  const [resetModalUser, setResetModalUser] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetPasswordMasque, setResetPasswordMasque] = useState(true);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos de l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentProfile(String(userData.profile));
        setCurrentSocieteId(Number(userData.societe_id));
      } catch (e) {
        console.error("Erreur parse user:", e);
      }
    }
  }, []);

  // Fonction pour charger les utilisateurs
  const fetchUsers = useCallback(async (page = 1) => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Construire les paramètres de requête
      const params = new URLSearchParams({
        page: page,
        limit: pagination.limit,
        ...filters
      });

      // Supprimer les paramètres vides
      Object.keys(filters).forEach(key => {
        if (!filters[key]) params.delete(key);
      });

      const url = `${API_BASE_URL}/societes/users.php?id_societe=${id}&${params}`;

      const response = await axios.get(url, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        setSociete(data.societe);
        setUsers(data.users);
        setPagination(data.pagination);
        setStats(data.stats);
        
        // Mettre à jour l'URL avec les filtres
        const newSearch = params.toString();
        if (location.search !== `?${newSearch}`) {
          navigate(`?${newSearch}`, { replace: true });
        }
      } else {
        setError(response.data.message || "Erreur lors du chargement des utilisateurs.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response) {
        const { status, data } = err.response;
        
        if (status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
        } else if (status === 403) {
          setError("Permission refusée. Vous ne pouvez consulter que les utilisateurs de votre propre société.");
        } else if (status === 404) {
          setError("Société non trouvée.");
        } else if (status === 400) {
          setError(`Données invalides: ${data?.message || ""}`);
        } else if (status === 500) {
          setError(`Erreur serveur: ${data?.message || ""}`);
        } else {
          setError(`Erreur ${status}: ${data?.message || "Impossible de charger les utilisateurs."}`);
        }
      } else if (err.request) {
        setError("Serveur inaccessible. Vérifiez votre connexion.");
      } else {
        setError("Erreur de configuration: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, filters, pagination.limit, location.search]);

  // Charger les utilisateurs au montage ou changement de filtre
  useEffect(() => {
    fetchUsers(pagination.page);
  }, [fetchUsers, pagination.page]);

  // Gérer les changements de filtres
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Appliquer les filtres (réinitialise à la page 1)
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      search: '',
      profile: '',
      statut: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    navigate(`/societes/${id}/users`);
  };

  // Changer de page
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.pages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  // Obtenir le badge de profil
  const getProfileBadge = (profile) => {
    const profileValue = parseInt(profile);
    
    switch (profileValue) {
      case 2: // Contrôleur
        return <span className="badge fw-semibold" style={{ 
          backgroundColor: '#fff8e1',
          color: '#ff8f00',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>Contrôleur</span>;
      case 3: // Gestionnaire
        return <span className="badge fw-semibold" style={{ 
          backgroundColor: '#E3F2FD',
          color: COLORS.primary,
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>Gestionnaire</span>;
      case 4: // Agent
        return <span className="badge fw-semibold" style={{
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>Agent</span>;
      case 5: // Poste de Contrôle
        return <span className="badge fw-semibold" style={{
          backgroundColor: '#f3e5f5',
          color: '#7b1fa2',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>Poste de Contrôle</span>;
      default:
        return <span className="badge fw-semibold" style={{ 
          backgroundColor: '#f5f5f5',
          color: '#757575',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>Inconnu</span>;
    }
  };

  // Obtenir le badge de statut
  const getStatusBadge = (status) => {
    const statusValue = parseInt(status);
    return statusValue === 1 
      ? <span className="badge fw-semibold" style={{ 
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>
          <i className="bi bi-check-circle me-1"></i>Actif
        </span>
      : <span className="badge fw-semibold" style={{ 
          backgroundColor: '#f5f5f5',
          color: '#757575',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem'
        }}>
          <i className="bi bi-x-circle me-1"></i>Inactif
        </span>;
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Non disponible";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Voir les détails d'un utilisateur
  const viewUserDetails = (userId) => {
    navigate(`/utilisateurs/details/${userId}`);
  };

  // Réinitialisation du mot de passe : l'admin peut réinitialiser n'importe
  // quel utilisateur de la société (Contrôleur, Gestionnaire, Agent, Poste
  // de Contrôle) — voir users/reset_password.php.
  const openResetModal = (user) => {
    setResetModalUser(user);
    setResetResult(null);
  };

  const closeResetModal = () => {
    setResetModalUser(null);
    setResetResult(null);
    setResetPasswordMasque(true);
  };

  const handleResetPassword = async () => {
    if (!resetModalUser) return;

    setResettingId(resetModalUser.id_utilisateur);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/users/reset_password.php`,
        { id: resetModalUser.id_utilisateur },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setResetResult(response.data);
    } catch (err) {
      setResetResult({
        success: false,
        message: err.response?.data?.message || "Erreur technique. Veuillez réessayer.",
      });
    } finally {
      setResettingId(null);
    }
  };

  // Vérifier si l'utilisateur peut ajouter un utilisateur
  const canAddUser = () => {
    // Seul le Super Admin peut ajouter des utilisateurs
    return currentProfile === "1";
  };

  // Vérifier si l'utilisateur peut modifier un utilisateur
  const canEditUser = () => {
    // Super Admin peut modifier tous les utilisateurs
    // Contrôleur peut modifier les utilisateurs de sa société
    return currentProfile === "1" || currentProfile === "2";
  };

  if (loading && !societe) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Utilisateurs de la société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/societes">Sociétés</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des utilisateurs...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !societe) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Utilisateurs de la société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/societes">Sociétés</Link>
              </li>
              <li className="breadcrumb-item active">Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-danger alert-dismissible fade show" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error || "Société non trouvée"}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <button 
                      className="btn fw-semibold"
                      onClick={() => navigate("/societes")}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour à la liste des sociétés
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Utilisateurs de la société</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/societes">Sociétés</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/societes/details/${id}`}>{societe.nom_societe}</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Utilisateurs</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* En-tête avec informations de la société */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                      width: '60px',
                      height: '60px',
                      backgroundColor: COLORS.primary,
                      color: COLORS.white
                    }}>
                      <i className="bi bi-building fs-3"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-1" style={{ color: COLORS.primary, fontWeight: '600' }}>
                        {societe.nom_societe}
                      </h5>
                      
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn fw-semibold"
                      onClick={() => navigate(`/societes/details/${id}`)}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-1"></i>
                      Retour aux détails
                    </button>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cartes de statistiques */}
        {stats && (
          <div className="row mb-4">
            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid ${COLORS.primary}`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: COLORS.primary,
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Total
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.total}
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-people fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: COLORS.success,
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Actifs
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.actifs}
                      </div>
                      <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                        {stats.total > 0 ? Math.round((stats.actifs / stats.total) * 100) : 0}%
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#e8f5e9',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-check-circle fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid #757575`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: '#757575',
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Inactifs
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.inactifs}
                      </div>
                      <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                        {stats.total > 0 ? Math.round((stats.inactifs / stats.total) * 100) : 0}%
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#f5f5f5',
                      color: '#757575'
                    }}>
                      <i className="bi bi-x-circle fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: COLORS.warning,
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Contrôleurs
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.controleurs}
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#fff8e1',
                      color: COLORS.warning
                    }}>
                      <i className="bi bi-shield-check fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid ${COLORS.secondary}`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: COLORS.secondary,
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Gestionnaires
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.gestionnaires}
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.secondary
                    }}>
                      <i className="bi bi-person-badge fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{ 
                        fontSize: '0.75rem',
                        color: COLORS.success,
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Agents
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.agents}
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#e8f5e9',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-person fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-2 col-md-4 mb-3">
              <div className="card" style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                borderLeft: `4px solid #7b1fa2`
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase" style={{
                        fontSize: '0.75rem',
                        color: '#7b1fa2',
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Poste de Contrôle
                      </div>
                      <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                        {stats.postes_controle || 0}
                      </div>
                    </div>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#f3e5f5',
                      color: '#7b1fa2'
                    }}>
                      <i className="bi bi-display fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title mb-4" style={{ 
                  color: COLORS.primary,
                  fontWeight: '600',
                  borderBottom: `1px solid ${COLORS.lightBg}`,
                  paddingBottom: '12px'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-funnel"></i>
                    </div>
                    Filtres de recherche
                  </div>
                </h5>
                
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label d-flex align-items-center" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: COLORS.lightBg,
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-search"></i>
                      </div>
                      Recherche
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nom, prénom, email..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3">
                    <label className="form-label d-flex align-items-center" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: COLORS.lightBg,
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-person-badge"></i>
                      </div>
                      Profil
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-funnel"></i>
                      </span>
                      <select
                        className="form-select"
                        value={filters.profile}
                        onChange={(e) => handleFilterChange('profile', e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      >
                        <option value="">Tous les profils</option>
                        <option value="2">Contrôleur</option>
                        <option value="3">Gestionnaire</option>
                        <option value="4">Agent</option>
                        <option value="5">Poste de Contrôle</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="col-md-3">
                    <label className="form-label d-flex align-items-center" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: COLORS.lightBg,
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-circle-fill"></i>
                      </div>
                      Statut
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-check-circle"></i>
                      </span>
                      <select
                        className="form-select"
                        value={filters.statut}
                        onChange={(e) => handleFilterChange('statut', e.target.value)}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText
                        }}
                      >
                        <option value="">Tous les statuts</option>
                        <option value="1">Actifs seulement</option>
                        <option value="0">Inactifs seulement</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="col-md-2 d-flex align-items-end">
                    <div className="d-flex gap-2 w-100">
                      <button 
                        className="btn fw-semibold flex-fill"
                        onClick={applyFilters}
                        disabled={loading}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          color: COLORS.white,
                          borderRadius: '8px',
                          padding: '10px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(-2px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.borderColor = COLORS.primary;
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <>
                            <i className="bi bi-funnel me-1"></i>
                            Filtrer
                          </>
                        )}
                      </button>
                      <button 
                        className="btn fw-semibold"
                        onClick={resetFilters}
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.color = COLORS.white;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = COLORS.primary;
                          }
                        }}
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Note sur les profils */}
                
              </div>
            </div>
          </div>
        </div>

        {/* Liste des utilisateurs */}
        <div className="row">
          <div className="col-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600'
                  }}>
                    Liste des utilisateurs
                  </h5>
                  {pagination.total > 0 && (
                    <span className="badge fw-semibold" style={{ 
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '0.9rem'
                    }}>
                      <i className="bi bi-people me-1"></i>
                      {pagination.total} utilisateur{pagination.total !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des utilisateurs...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-people fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun utilisateur trouvé</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      {Object.values(filters).some(f => f) 
                        ? "Aucun utilisateur ne correspond à vos critères de recherche. Essayez de modifier vos filtres."
                        : "Cette société n'a pas encore d'utilisateurs. Vous pouvez commencer par en ajouter un."}
                    </p>
                    {canAddUser() && !Object.values(filters).some(f => f) && (
                      <button 
                        className="btn fw-semibold mt-3"
                        onClick={() => navigate(`/societes/create?societe=${id}`)}
                        style={{
                          color: COLORS.white,
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.borderColor = COLORS.secondary;
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.borderColor = COLORS.primary;
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Ajouter le premier utilisateur
                      </button>
                    )}
                    {Object.values(filters).some(f => f) && (
                      <button 
                        className="btn fw-semibold mt-3 ms-2"
                        onClick={resetFilters}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-lg me-1"></i>
                        Effacer les filtres
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Utilisateur</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Contact</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Profil</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', width: '160px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user) => (
                            <tr 
                              key={user.id_utilisateur} 
                              className="cursor-pointer"
                              style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}
                              onClick={() => viewUserDetails(user.id_utilisateur)}
                            >
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <div 
                                      className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                      style={{ 
                                        width: '44px', 
                                        height: '44px', 
                                        fontSize: '15px',
                                        backgroundColor: COLORS.primary,
                                        boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                      }}
                                    >
                                      {user.prenom_utilisateur?.charAt(0) || ''}{user.nom_utilisateur?.charAt(0) || ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ 
                                      color: COLORS.primary, 
                                      fontWeight: '500',
                                      fontSize: '0.95rem'
                                    }}>
                                      {user.nom_utilisateur} {user.prenom_utilisateur}
                                    </div>
                                    
                                  </div>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div>
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.secondary,
                                      fontSize: '0.7rem'
                                    }}>
                                      <i className="bi bi-envelope"></i>
                                    </div>
                                    <a 
                                      href={`mailto:${user.email_utilisateur}`} 
                                      className="text-decoration-none"
                                      style={{ color: COLORS.secondary, fontSize: '0.85rem' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {user.email_utilisateur}
                                    </a>
                                  </div>
                                  {user.tel_utilisateur && (
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-telephone"></i>
                                      </div>
                                      <a 
                                        href={`tel:${user.tel_utilisateur}`} 
                                        className="text-decoration-none"
                                        style={{ color: COLORS.secondary, fontSize: '0.85rem' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {user.tel_utilisateur}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                {getProfileBadge(user.profile)}
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                {getStatusBadge(user.statut_utilisateur)}
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                  {formatDate(user.created_at)}
                                </small>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex gap-2">
                                  <button 
  className="btn fw-semibold"
  onClick={(e) => {
    e.stopPropagation();
    
    navigate(`/users/details/${user.id_utilisateur}`);
    
  }}
  title="Voir détails"
  style={{
    color: COLORS.primary,
    borderColor: COLORS.primary,
    backgroundColor: 'transparent',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.85rem',
    transition: 'all 0.3s ease'
  }}
  onMouseEnter={(e) => {
    e.target.style.backgroundColor = COLORS.primary;
    e.target.style.color = COLORS.white;
  }}
  onMouseLeave={(e) => {
    e.target.style.backgroundColor = 'transparent';
    e.target.style.color = COLORS.primary;
  }}
>
  <i className="bi bi-eye"></i>
</button>
                                  <button
  className="btn fw-semibold"
  onClick={(e) => {
    e.stopPropagation();
    openResetModal(user);
  }}
  title="Réinitialiser le mot de passe"
  style={{
    color: '#4caf50',
    borderColor: '#4caf50',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.85rem',
    transition: 'all 0.3s ease'
  }}
  onMouseEnter={(e) => {
    e.target.style.backgroundColor = '#4caf50';
    e.target.style.color = COLORS.white;
  }}
  onMouseLeave={(e) => {
    e.target.style.backgroundColor = 'transparent';
    e.target.style.color = '#4caf50';
  }}
>
  <i className="bi bi-key"></i>
</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                      <nav className="mt-4">
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link fw-semibold" 
                              onClick={(e) => {
                                e.stopPropagation();
                                goToPage(pagination.page - 1);
                              }}
                              disabled={pagination.page === 1}
                              style={{
                                color: pagination.page === 1 ? '#B0BEC5' : COLORS.primary,
                                borderColor: '#e0e0e0',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                margin: '0 4px'
                              }}
                            >
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>
                          
                          {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            let pageNum;
                            if (pagination.pages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.pages - 2) {
                              pageNum = pagination.pages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }
                            
                            return (
                              <li key={pageNum} className="page-item">
                                <button 
                                  className="page-link fw-semibold" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    goToPage(pageNum);
                                  }}
                                  style={{
                                    backgroundColor: pagination.page === pageNum ? COLORS.primary : 'transparent',
                                    borderColor: pagination.page === pageNum ? COLORS.primary : '#e0e0e0',
                                    color: pagination.page === pageNum ? COLORS.white : COLORS.primary,
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    margin: '0 4px',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (pagination.page !== pageNum) {
                                      e.target.style.backgroundColor = '#E3F2FD';
                                      e.target.style.borderColor = COLORS.secondary;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (pagination.page !== pageNum) {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.borderColor = '#e0e0e0';
                                    }
                                  }}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          
                          <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link fw-semibold" 
                              onClick={(e) => {
                                e.stopPropagation();
                                goToPage(pagination.page + 1);
                              }}
                              disabled={pagination.page === pagination.pages}
                              style={{
                                color: pagination.page === pagination.pages ? '#B0BEC5' : COLORS.primary,
                                borderColor: '#e0e0e0',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                margin: '0 4px'
                              }}
                            >
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                        <div className="text-center mt-3">
                          <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px',
                              margin: '0 8px'
                            }}>
                              Page {pagination.page} sur {pagination.pages}
                            </span>
                            <span className="mx-2">•</span>
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px',
                              margin: '0 8px'
                            }}>
                              Total: {pagination.total} utilisateur{pagination.total !== 1 ? 's' : ''}
                            </span>
                          </small>
                        </div>
                      </nav>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODAL RÉINITIALISATION MOT DE PASSE */}
      {resetModalUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#fff3e0' }}>
                <h5 className="modal-title" style={{ color: COLORS.warning }}>
                  <i className="bi bi-key me-2"></i>
                  Réinitialiser le mot de passe
                </h5>
                <button type="button" className="btn-close" onClick={closeResetModal}></button>
              </div>
              <div className="modal-body">
                {!resetResult ? (
                  <>
                    <p style={{ color: COLORS.darkText }}>
                      Réinitialiser le mot de passe de <strong>{resetModalUser.prenom_utilisateur} {resetModalUser.nom_utilisateur}</strong> ?
                    </p>
                    <p className="text-muted small mb-0">
                      Un nouveau mot de passe temporaire sera généré et envoyé par email si possible.
                    </p>
                  </>
                ) : resetResult.success ? (
                  <>
                    <div className="alert alert-success mb-3">
                      <i className="bi bi-check-circle me-2"></i>
                      {resetResult.message || "Mot de passe réinitialisé avec succès."}
                    </div>
                    {resetResult.generated_password && (
                      <div>
                        <p className="text-muted small mb-2">
                          {resetResult.warning || "L'email n'a pas pu être envoyé."} Communiquez ce mot de passe à l'utilisateur :
                        </p>
                        <div className="d-flex align-items-center gap-2">
                          <code className="p-2 rounded flex-grow-1" style={{ backgroundColor: COLORS.lightBg, fontSize: '1rem', border: '1px solid #e0e0e0' }}>
                            {resetPasswordMasque ? '•'.repeat(Math.max(resetResult.generated_password.length, 8)) : resetResult.generated_password}
                          </code>
                          <button
                            type="button"
                            className="btn btn-sm fw-semibold"
                            onClick={() => setResetPasswordMasque(!resetPasswordMasque)}
                            style={{ backgroundColor: 'transparent', border: '1px solid #6c757d', color: '#6c757d', borderRadius: '6px' }}
                            title={resetPasswordMasque ? 'Afficher' : 'Masquer'}
                          >
                            <i className={`bi ${resetPasswordMasque ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm fw-semibold"
                            onClick={() => navigator.clipboard?.writeText(resetResult.generated_password)}
                            style={{ color: COLORS.primary, borderColor: COLORS.primary, borderRadius: '6px' }}
                          >
                            <i className="bi bi-clipboard me-1"></i>
                            Copier
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-danger mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {resetResult.message || "Échec de la réinitialisation."}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {!resetResult ? (
                  <>
                    <button className="btn btn-secondary" onClick={closeResetModal}>Annuler</button>
                    <button
                      className="btn btn-warning"
                      onClick={handleResetPassword}
                      disabled={resettingId === resetModalUser.id_utilisateur}
                    >
                      {resettingId === resetModalUser.id_utilisateur ? 'Traitement...' : 'Réinitialiser'}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-secondary" onClick={closeResetModal}>Fermer</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.align-items-center.justify-content-between {
            flex-direction: column;
            align-items: flex-start !important;
          }
          
          .d-flex.gap-2 {
            margin-top: 16px;
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .col-md-4,
          .col-md-3,
          .col-md-2 {
            margin-bottom: 16px;
          }
          
          .pagination {
            flex-wrap: wrap;
          }
          
          .pagination .page-link {
            margin: 4px !important;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Style pour les liens */
        a {
          transition: color 0.2s ease;
        }
        
        a:hover {
          color: ${COLORS.primary} !important;
        }
      `}</style>
    </Layout>
  );
};

export default SocieteUsers;